import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  KbArticle, KbCategoryCount, KbInput,
  listArticles, listCategoryCounts, getArticle, createArticle, updateArticle, deleteArticle,
} from '../services/knowledgeBaseApi';

interface KbState {
  items: KbArticle[];
  current: KbArticle | null;
  categories: KbCategoryCount[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchList: (filters?: { category?: string; search?: string }) => Promise<void>;
  fetchCategories: () => Promise<void>;
  fetchOne: (id: number | string) => Promise<KbArticle>;
  create: (input: KbInput) => Promise<KbArticle>;
  update: (id: number | string, input: Partial<KbInput>) => Promise<KbArticle>;
  remove: (id: number | string) => Promise<void>;
}

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useKnowledgeBaseStore = create<KbState>()(
  devtools(
    (set) => ({
      items: [],
      current: null,
      categories: [],
      loading: false,
      saving: false,
      error: null,
      fetchList: async (filters) => {
        set({ loading: true, error: null });
        try { set({ items: await listArticles(filters), loading: false }); }
        catch (err) { set({ loading: false, error: toErr(err) }); }
      },
      fetchCategories: async () => {
        try { set({ categories: await listCategoryCounts() }); }
        catch (err) { set({ error: toErr(err) }); }
      },
      fetchOne: async (id) => {
        set({ loading: true, error: null });
        try {
          const article = await getArticle(id);
          set({ current: article, loading: false });
          return article;
        } catch (err) {
          set({ loading: false, error: toErr(err) });
          throw err;
        }
      },
      create: async (input) => {
        set({ saving: true, error: null });
        try {
          const created = await createArticle(input);
          set((s) => ({ items: [created, ...s.items], saving: false }));
          return created;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      update: async (id, input) => {
        set({ saving: true, error: null });
        try {
          const updated = await updateArticle(id, input);
          set((s) => ({
            items: s.items.map((a) => (String(a.id) === String(id) ? updated : a)),
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      remove: async (id) => {
        set({ saving: true, error: null });
        try {
          await deleteArticle(id);
          set((s) => ({ items: s.items.filter((a) => String(a.id) !== String(id)), saving: false }));
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
    }),
    { name: 'kb-store' }
  )
);
